import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Environment from "../utility/environment";
import { Enterprise } from "../utility/types";
import {
  clearStoredEnterpriseId,
  readStoredEnterpriseId,
  resolveSelectedEnterprise,
  writeStoredEnterpriseId,
} from "../utility/selectedEnterprise";

export type User = {
  id: string;
  name: string;
  email: string;
  role: string;
  cargo: string;
  photo: string;
  username: string;
  locale: string;
  enterprises: Enterprise[]; // Adiciona o campo de empresas
}

/** enterpriseId → pageKey → access (aligned with backend tb_user_enterprise_page_access). */
export type PageAccessByEnterprise = Record<string, Record<string, string>>;

interface AuthContextType {
  accessToken: string | null;
  user: User | null;
  pageAccess: PageAccessByEnterprise;
  login: (email: string, password: string, remember: boolean) => Promise<void>;
  logout: () => void;
  updateNotifications: boolean;
  setUpdateNotifications: React.Dispatch<React.SetStateAction<boolean>>;
  actualEnterprise: Enterprise | null; // Adiciona o campo de empresa atual
  setActualEnterprise: (enterprise: Enterprise | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const [updateNotifications, setUpdateNotifications] = useState(false);
  const [pageAccess, setPageAccess] = useState<PageAccessByEnterprise>({});
  const [actualEnterprise, setActualEnterpriseState] = useState<Enterprise | null>(null);
  const selectedEnterpriseIdRef = useRef<string | null>(null);
  const userIdRef = useRef<string | null>(null);

  const applyEnterpriseSelection = useCallback((
    userId: string,
    enterprises: Enterprise[],
    preferredId?: string | null,
  ) => {
    const resolved = resolveSelectedEnterprise(
      enterprises,
      preferredId ?? selectedEnterpriseIdRef.current ?? readStoredEnterpriseId(userId),
    );
    selectedEnterpriseIdRef.current = resolved?.id ?? null;
    if (resolved?.id) {
      writeStoredEnterpriseId(userId, resolved.id);
    }
    setActualEnterpriseState(resolved);
    return resolved;
  }, []);

  const setActualEnterprise = useCallback((enterprise: Enterprise | null) => {
    selectedEnterpriseIdRef.current = enterprise?.id ?? null;
    if (userIdRef.current && enterprise?.id) {
      writeStoredEnterpriseId(userIdRef.current, enterprise.id);
    }
    setActualEnterpriseState(enterprise);
  }, []);

  const refreshAccessTokenRef = useRef<() => Promise<void>>(async () => {});

  refreshAccessTokenRef.current = async () => {
    try {
      const response = await fetch(`${Environment.VITE_API_URL}/refresh`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        const enterprises: Enterprise[] = data.enterprise || [];
        userIdRef.current = data.id;
        setAccessToken(data.access_token);
        setUser({
          id: data.id,
          name: data.name,
          email: data.email,
          role: data.role,
          cargo: data.cargo,
          photo: data.photo,
          username: data.username,
          locale: data.locale ?? 'en',
          enterprises,
        });
        setPageAccess(data.page_access ?? {});
        setLoading(false);
        applyEnterpriseSelection(data.id, enterprises);
      } else {
        setLoading(false);
        console.error("Erro ao atualizar token");
        setAccessToken(null);
      }
    } catch {
      setAccessToken(null);
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      await refreshAccessTokenRef.current();
    };
    void initializeAuth();
    const id = setInterval(() => {
      void refreshAccessTokenRef.current();
    }, 1000 * 60 * 10);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const token = new URLSearchParams(location.search).get("token");
    const attemptedPath = location.pathname;
    if (attemptedPath === "/reset-password" && token) {
      return;
    }

    if (!loading && !accessToken && !token) {
      if (attemptedPath !== "/login") {
        localStorage.setItem("redirectAfterLogin", attemptedPath);
      }
      navigate("/login");
    }
  }, [accessToken, loading, location.pathname, location.search, navigate]);

  const login = async (email: string, password: string, remember: boolean) => {
    const formData = new URLSearchParams();
    formData.append("username", email);
    formData.append("password", password);

    const response = await fetch(`${Environment.VITE_API_URL}/token?remember=${remember ? "True" : "False"}`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      credentials: "include",
      body: formData.toString(),
    });

    if (!response.ok) {
      throw new Error("Invalid Credentials");
    }

    const data = await response.json();
    const enterprises: Enterprise[] = data.enterprise || [];
    userIdRef.current = data.id;
    setAccessToken(data.access_token);
    setUser({
      id: data.id,
      name: data.name,
      email: data.email,
      role: data.role,
      cargo: data.cargo,
      photo: data.photo,
      username: data.username,
      locale: data.locale ?? 'en',
      enterprises,
    });
    setPageAccess(data.page_access ?? {});
    setLoading(false);
    applyEnterpriseSelection(data.id, enterprises);
    const redirectPath = localStorage.getItem("redirectAfterLogin") || "/";
    localStorage.removeItem("redirectAfterLogin");
    navigate(redirectPath);
  };

  const logout = async () => {
    const loggingOutUserId = userIdRef.current;
    try {
      await fetch(`${Environment.VITE_API_URL}/logout`, { method: "POST", credentials: "include" });
    } catch (e) {
      console.warn("Erro ao fazer logout no servidor:", e);
    }
    clearStoredEnterpriseId(loggingOutUserId ?? undefined);
    selectedEnterpriseIdRef.current = null;
    userIdRef.current = null;
    setAccessToken(null);
    setUser(null);
    setPageAccess({});
    setActualEnterpriseState(null);
    navigate("/login");
  };

  const locationWithoutAuth = location.pathname === "/login" || location.pathname === "/forgot-password" || location.pathname === "/reset-password";
  if (loading || (!accessToken && !locationWithoutAuth && !new URLSearchParams(location.search).get("token"))) {
    return null;
  }

  return (
    <AuthContext.Provider value={{
      accessToken,
      user,
      pageAccess,
      login,
      logout,
      updateNotifications,
      setUpdateNotifications,
      actualEnterprise,
      setActualEnterprise,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  return context;
};
